import React, { useState, useContext, useEffect } from "react";
import {
    Box,
    Button,
    Typography,
    CircularProgress,
    DialogTitle,
    Dialog,
    DialogContent,
    DialogActions,
    Alert,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { Add as AddIcon } from "@mui/icons-material";
import {
    type ShiftTask,
    type ICreateTask,
    type IUpdateTask,
    TaskCategory,
    PrinterLabelType,
    api,
    PrinterLabel,
} from "@/logic/api";
import { AddEditTaskModal } from "./AddEditTaskModal";
import { TaskListItem } from "./TaskListItem";
import { useGetShiftTasks } from "@/hooks/useGetShiftTasks";
import { useCreateShiftTask } from "@/hooks/useCreateShiftTask";
import { useUpdateShiftTask } from "@/hooks/useUpdateShiftTask";
import { useDeleteShiftTask } from "@/hooks/useDeleteShiftTask";
import { useLabelPrinter, ZebraLabelPrinter } from "@/components/labels";
import { SessionContext } from "@/utility/useSession";
import { retrieveValue } from "@/utility/storage";
import { promisifyApi } from "@/utility/utils";
import { ReleaseFlag, useReleaseManagement } from "@/utility/feature-management";
import { DriverAvailabilityRuleOptions } from "./DriverAvailabilityRuleOptions";
import { IPreferences } from "@/logic/api";

interface DriverAvailabilityManagementCardProps {
    onSave: (data: Partial<IPreferences>) => Promise<boolean>;
}

export const DriverAvailabilityManagementCard: React.FC<DriverAvailabilityManagementCardProps> = ({ onSave }) => {
    const { t } = useTranslation();
    const { mergeZPLParameters } = useLabelPrinter();
    const printLabelApi = promisifyApi(api.hooks.printerLabel.get());
    const session = useContext(SessionContext);
    const isDeferLabelValidationReleased = useReleaseManagement(ReleaseFlag.releaseDeferLabelValidation);
    const isDriverAvailabilitySettingsReleased = useReleaseManagement(ReleaseFlag.releaseSettingsDriverAvailability);

    const [showAddModal, setShowAddModal] = useState(false);
    const [editingTask, setEditingTask] = useState<ShiftTask | undefined>(
        undefined
    );
    const [deletingTask, setDeletingTask] = useState<ShiftTask | undefined>(
        undefined
    );
    const [taskLabel, setTaskLabel] = useState<PrinterLabel | null>(null);
    const [printError, setPrintError] = useState<Error | null>(null);
    const [isPrinting, setIsPrinting] = useState(false);
    const [printingTaskKey, setPrintingTaskKey] = useState<string | null>(null);


    const { data, isLoading, error } = useGetShiftTasks();
    const createTaskMutation = useCreateShiftTask();
    const updateTaskMutation = useUpdateShiftTask();
    const deleteTaskMutation = useDeleteShiftTask();

    const tasks = data ?? [];

    const isSaving =
        createTaskMutation.isPending ||
        updateTaskMutation.isPending ||
        deleteTaskMutation.isPending;

    const errorMessage =
        (createTaskMutation.error?.message ? t("task.errors.createFailed", { error: createTaskMutation.error.message }) : "") ||
        (updateTaskMutation.error?.message ? t("task.errors.updateFailed", { error: updateTaskMutation.error.message }) : "") ||
        (deleteTaskMutation.error?.message ? t("task.errors.deleteFailed", { error: deleteTaskMutation.error.message }) : "") ||
        (error?.message || "");


    useEffect(() => {
        const fetchLabel = async () => {
            if (isDeferLabelValidationReleased) {
                try {
                    const label = await printLabelApi.send(
                        session.current.context,
                        { type: PrinterLabelType.task }
                    );
                    if (label) {
                        setTaskLabel(label);
                    }
                } catch {
                    // we don't want to show the errors here as this fetch happens on mount and there might not be labels
                    // set up or a printer availabile
                }
            } else {
                try {
                    setPrintError(null);
                    const label = await printLabelApi.send(
                        session.current.context,
                        { type: PrinterLabelType.task }
                    );
                    if (!label) {
                        throw new Error(t("labels.labelNotFound", { type: PrinterLabelType.task }));
                    }
                    if (!label.zpl) {
                        throw new Error(t("labels.labelNotConfig", { type: PrinterLabelType.task }));
                    }
                    setTaskLabel(label);
                } catch (err) {
                    setPrintError(err as Error);
                }
            }
        };
        fetchLabel();
    }, []);

    const nonDrivingTasks: ShiftTask[] = [];
    const systemTasks: ShiftTask[] = [];
    for (const task of tasks) {
        if (task.category === TaskCategory.NonDriving) {
            nonDrivingTasks.push(task);
        } else {
            systemTasks.push(task);
        }
    }

    const handleAddTask = async (data: ICreateTask | IUpdateTask) => {
        // Only proceed if data is ICreateTask (no key property)
        if ("key" in data) return;

        try {
            await createTaskMutation.mutateAsync({
                title: data.title,
                category: TaskCategory.NonDriving,
            });
            setShowAddModal(false);
        } catch (err) {
            console.error("Error creating task:", err);
        }
    };

    const handleEditTask = async (data: ICreateTask | IUpdateTask) => {
        // Only proceed if data is IUpdateTask (has key property)
        if (!("key" in data) || !editingTask?.key) return;

        try {
            await updateTaskMutation.mutateAsync({
                key: data.key,
                title: data.title,
                category: TaskCategory.NonDriving,
            });
            setEditingTask(undefined);
        } catch (err) {
            console.error("Error updating task:", err);
        }
    };

    const handleDeleteTask = async () => {
        if (!deletingTask?.key) return;

        try {
            await deleteTaskMutation.mutateAsync({ key: deletingTask.key });
            setDeletingTask(undefined);
        } catch (err) {
            console.error("Error deleting task:", err);
        }
    };

    const handlePrintTask = async (task: ShiftTask) => {
        if (!task?.key || !task?.title) return;

        if (isDeferLabelValidationReleased) {
            const defaultPrinter = retrieveValue("defaultLabelPrinterUID", "");
            if (!defaultPrinter) {
                setPrintError(new Error(t("labels.noPrintersFound")));
                return;
            }
            if (!taskLabel) {
                setPrintError(new Error(t("labels.labelNotFound", { type: PrinterLabelType.task })));
                return;
            }
            if (!taskLabel.zpl) {
                setPrintError(new Error(t("labels.labelNotConfig", { type: PrinterLabelType.task })));
                return;
            }
        } else {
            if (!taskLabel) return;
        }

        const taskParams = {
            taskKey: task.key,
            taskTitle: task.title,
        };

        setPrintError(null);
        setPrintingTaskKey(task.key);
        setIsPrinting(true);

        try {
            const defaultPrinter = retrieveValue("defaultLabelPrinterUID", "");
            await ZebraLabelPrinter.sendToPrinter(
                defaultPrinter,
                mergeZPLParameters(taskLabel.zpl, taskParams)
            );
        } catch (err) {
            const e = err instanceof Error ? err : new Error(String(err));
            setPrintError(e);
            console.error("Error printing task label:", e);
        } finally {
            setPrintingTaskKey(null);
            setIsPrinting(false);
        }
    };



    if (isLoading) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box data-testid="driver-availability-management-card">

            {isDriverAvailabilitySettingsReleased && <DriverAvailabilityRuleOptions onSave={onSave} />}
            {isDriverAvailabilitySettingsReleased && <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 1.5 }}>
                    {t("task.management.system")}
                </Typography>
            </Box>}
            {systemTasks.length === 0 ? (
                <Box
                    sx={{
                        textAlign: "center",
                        py: 6,
                        border: "1px dashed #e0e0e0",
                        borderRadius: 1,
                        bgcolor: "#f9f9f9",
                    }}
                >
                    <Typography variant="body1" color="text.secondary">
                        {t("task.management.noTasks")}
                    </Typography>
                </Box>
            ) : (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    {systemTasks.map((task) => (
                        <TaskListItem
                            key={task.id}
                            task={task}
                            onEdit={(task) => {
                                createTaskMutation.reset();
                                updateTaskMutation.reset();
                                deleteTaskMutation.reset();
                                setEditingTask(task);
                            }}
                            onDelete={(task) => {
                                createTaskMutation.reset();
                                updateTaskMutation.reset();
                                deleteTaskMutation.reset();
                                setDeletingTask(task);
                            }}
                            onPrint={(task) => {
                                handlePrintTask(task);
                            }}
                            disableDelete={true}
                            disableEdit={true}
                            disablePrint={isPrinting || printingTaskKey === task.key}
                            isPrinting={printingTaskKey === task.key}
                        />
                    ))}
                </Box>
            )}
            <Box sx={{ my: 4 }} />
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 3,
                }}
            >
                <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 0.5 }}>
                        {t("task.management.nonDriving")}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {t("task.management.description")}
                    </Typography>
                </Box>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => {
                        createTaskMutation.reset();
                        updateTaskMutation.reset();
                        deleteTaskMutation.reset();
                        setShowAddModal(true);
                    }}
                    disabled={isSaving}
                    sx={{
                        fontWeight: "bold",
                        backgroundColor: (theme) => theme.palette.brand.main,
                        color: (theme) => theme.palette.primary.contrastText,
                        transition:
                            "transform 150ms ease, background-color 150ms cubic-bezier(.4,0,.2,1), box-shadow 150ms ease",
                        "&:hover": {
                            backgroundColor: (theme) => theme.palette.brand.dark,
                            color: (theme) => theme.palette.primary.contrastText,
                        },
                        "&:active": {
                            transform: "scale(0.97)",
                        },
                        "&:focus-visible": {
                            boxShadow: (theme) =>
                                `0 0 0 3px ${theme.palette.primary.main}40`,
                        },
                    }}
                    disableRipple
                    disableElevation
                    data-testid="add-task-button"
                >
                    {t("task.management.addTask")}
                </Button>
            </Box>
            {printError && (
                <Alert
                    severity="error"
                    sx={{ mb: 3 }}
                    onClose={() => setPrintError(null)}
                >
                    {printError.message || t("task.errors.printFailed")}
                </Alert>
            )}
            {errorMessage && (
                <Alert
                    severity="error"
                    sx={{ mb: 3 }}
                    onClose={() => {
                        createTaskMutation.reset();
                        updateTaskMutation.reset();
                        deleteTaskMutation.reset();
                    }}
                >
                    {errorMessage}
                </Alert>
            )}
            {nonDrivingTasks.length === 0 ? (
                <Box
                    sx={{
                        textAlign: "center",
                        py: 6,
                        border: "1px dashed #e0e0e0",
                        borderRadius: 1,
                        bgcolor: "#f9f9f9",
                    }}
                >
                    <Typography variant="body1" color="text.secondary">
                        {t("task.management.noTasks")}
                    </Typography>
                </Box>
            ) : (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    {nonDrivingTasks.map((task) => (
                        <TaskListItem
                            key={task.id}
                            task={task}
                            onEdit={(task) => {
                                createTaskMutation.reset();
                                updateTaskMutation.reset();
                                deleteTaskMutation.reset();
                                setEditingTask(task);
                            }}
                            onDelete={(task) => {
                                createTaskMutation.reset();
                                updateTaskMutation.reset();
                                deleteTaskMutation.reset();
                                setDeletingTask(task);
                            }}
                            onPrint={(task) => {
                                handlePrintTask(task);
                            }}
                            disableDelete={isSaving}
                            disableEdit={isSaving}
                            disablePrint={isPrinting || printingTaskKey === task.key}
                            isPrinting={printingTaskKey === task.key}
                        />
                    ))}
                </Box>
            )}

            <AddEditTaskModal
                open={showAddModal || Boolean(editingTask)}
                onClose={() => {
                    setShowAddModal(false);
                    setEditingTask(undefined);
                }}
                onSave={editingTask ? handleEditTask : handleAddTask}
                task={editingTask}
                isLoading={isSaving}
                category={TaskCategory.NonDriving}
            />

            <Dialog
                open={Boolean(deletingTask)}
                onClose={() => setDeletingTask(undefined)}
                maxWidth="sm"
                fullWidth
                data-testid="delete-task-confirmation"
            >
                <DialogTitle>
                    <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                        {t("task.actions.confirmDelete")}
                    </Typography>
                </DialogTitle>
                <DialogContent>
                    <Typography>
                        {t("task.deleteDialog.confirmMessage", {
                            title: deletingTask?.title,
                        })}
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2 }}>
                    <Button
                        onClick={() => setDeletingTask(undefined)}
                        disabled={isSaving}
                        variant="text"
                        sx={{
                            transition:
                                "transform 150ms ease, background-color 150ms cubic-bezier(.4,0,.2,1), box-shadow 150ms ease",
                            "&:active": {
                                transform: "scale(0.97)",
                            },
                            "&:hover": {
                                backgroundColor: (theme) => theme.palette.action.hover,
                            },
                            "&:focus-visible": {
                                boxShadow: (theme) =>
                                    `0 0 0 3px ${theme.palette.primary.main}40`,
                            },
                        }}
                        disableRipple
                        disableElevation
                    >
                        {t("task.actions.cancel")}
                    </Button>
                    <Button
                        onClick={handleDeleteTask}
                        variant="contained"
                        color="error"
                        disabled={isSaving}
                        sx={{
                            fontWeight: "bold",
                            transition:
                                "transform 150ms ease, background-color 150ms cubic-bezier(.4,0,.2,1), box-shadow 150ms ease",
                            "&:active": {
                                transform: "scale(0.97)",
                            },
                            "&:focus-visible": {
                                boxShadow: (theme) =>
                                    `0 0 0 3px ${theme.palette.primary.main}40`,
                            },
                        }}
                        disableRipple
                        disableElevation
                        data-testid="confirm-delete-task"
                    >
                        {isSaving
                            ? t("task.actions.deleting")
                            : t("task.actions.deleteTaskButton")}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};